'use client';

// Deploy-boundary chunk-load recovery.
//
// When a new deployment goes live, a tab that has been open since the previous
// build requests JS/CSS chunks the new build no longer serves. Next's runtime
// throws a ChunkLoadError, which surfaces as the "Something went wrong" error
// boundary. A single full reload fetches the fresh asset manifest and the page
// self-heals — there is no application defect to fix, only stale assets.
//
// This error class is already dropped from Sentry by
// isLikelyNextStaticAssetDriftEvent, so auto-recovering in the boundary closes
// the remaining UX gap (coordinators briefly seeing the error page after a
// deploy) without re-introducing telemetry noise.

const RELOAD_AT_KEY = 'statura:chunk-reload-at';
// Block a second auto-reload within this window so a non-deploy failure (e.g.
// genuinely offline) cannot spin into a reload loop — after it elapses, the
// user sees the normal error boundary and its "Try again" button.
const RELOAD_WINDOW_MS = 20_000;

/** Heuristic match for a stale-deploy chunk / dynamic-import load failure. */
export function isChunkLoadError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  const message = (error as { message?: unknown } | null)?.message;
  const text = `${typeof name === 'string' ? name : ''} ${typeof message === 'string' ? message : ''}`;
  return (
    /\bChunkLoadError\b/.test(text) ||
    /Loading chunk [\w-]+ failed/i.test(text) ||
    /Loading CSS chunk [\w-]+ failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text)
  );
}

/**
 * If `error` looks like a deploy-boundary chunk failure, trigger a one-time
 * full reload and return true (the caller should render nothing further while
 * the navigation happens). Returns false for any non-chunk error, when a reload
 * was already attempted in the last RELOAD_WINDOW_MS, or when sessionStorage is
 * unavailable — in those cases the normal error boundary should render.
 */
export function maybeReloadOnChunkError(error: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Private mode / quota — cannot guard against a loop, so do not auto-reload.
    return false;
  }
  window.location.reload();
  return true;
}
